import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { isEmpty, isNil, noop } from 'lodash';
// Import Types
import type { CJVisualizationUnlockCondition, CJVisualizationUnlockUIModel, CJVisualizationStepOption } from 'types';
// Import Enums
import { CJVisualizationUnlockConditionTypeEnum } from 'enums';
// Import UI Components
import { Dropdown } from '@geneui/components';
// Import Components
import CJFilterCondition from 'components/CustomerJourney/forms/CJFilterCondition';
import CustomerJourneyVisualizationToggleSection from './CustomerJourneyVisualizationToggleSection';
import ValidatableDatePickerInput from 'components/common/ValidatableDatePickerInput';
// Import Hooks
import { usePartnerDateFormats } from 'hooks';
// Import Constants
import { l, LogicTypesValues, DateToISOStringFormat, CJVisualizationUnlockConditionTypeLabels } from 'constants/common';
// Import Styles
import styles from './customerJourneyVisualizationUnlock.module.scss';

interface Props {
    defaultValue?: Nilable<CJVisualizationUnlockUIModel>;
    node?: Nullable<AnyObject>;
    elements?: Nullable<AnyObject[]>;
    option?: Nullable<AnyObject>;
    collectedProperties?: AnyObject[];
    availableSteps?: CJVisualizationStepOption[];
    getUpdate?: (data: Partial<CJVisualizationUnlockUIModel>) => void;
}

const getDefaultCondition = (): CJVisualizationUnlockCondition => ({
    logic: LogicTypesValues.and,
    filters: [],
    isGroupFilter: true,
    isValid: false,
});

const CustomerJourneyVisualizationUnlock = (props: Props) => {
    const {
        defaultValue = null,
        node = null,
        elements = null,
        option = null,
        collectedProperties = [],
        availableSteps = [],
        getUpdate = noop,
    } = props;

    const { t } = useTranslation();
    const { partnerDateTimeWithoutSecondsFormat } = usePartnerDateFormats();

    const [isLocked, setIsLocked] = useState<boolean>(defaultValue?.isLocked === true);
    const [type, setType] = useState<Nullable<number>>(defaultValue?.type ?? null);
    const [stepUid, setStepUid] = useState<Nullable<string>>(defaultValue?.stepUid ?? null);
    const [condition, setCondition] = useState<CJVisualizationUnlockCondition>(
        defaultValue?.condition ?? getDefaultCondition(),
    );
    const [dateTime, setDateTime] = useState<Nullable<string>>(defaultValue?.dateTime ?? null);

    const typesData = useMemo(
        () =>
            [
                CJVisualizationUnlockConditionTypeEnum.PassSelectedStep,
                CJVisualizationUnlockConditionTypeEnum.CJCondition,
                CJVisualizationUnlockConditionTypeEnum.DateTime,
            ].map((value) => ({ value, label: t(CJVisualizationUnlockConditionTypeLabels[value]) })),
        [t],
    );

    const availableStepsKey = availableSteps.map((step) => step.value).join(',');
    const isSelectedStepAvailable = !isNil(stepUid) && availableSteps.some((step) => step.value === stepUid);
    const isDateTimeValid = !isEmpty(dateTime) && moment(dateTime, DateToISOStringFormat).isValid();

    const typeChangeHandler = (item) => {
        setType(item?.value ?? null);
    };

    const stepChangeHandler = (item) => {
        setStepUid(item?.value ?? null);
    };

    const conditionUpdateHandler = (conditionValue) => {
        setCondition((prev) => ({ ...prev, ...conditionValue }));
    };

    const dateTimeChangeHandler = (date) => {
        setDateTime(isNil(date) ? null : moment(date).format(DateToISOStringFormat));
    };

    useEffect(() => {
        let isValid = true;
        if (isLocked) {
            switch (type) {
                case CJVisualizationUnlockConditionTypeEnum.PassSelectedStep:
                    isValid = isSelectedStepAvailable;
                    break;
                case CJVisualizationUnlockConditionTypeEnum.CJCondition:
                    isValid = condition?.isValid === true;
                    break;
                case CJVisualizationUnlockConditionTypeEnum.DateTime:
                    isValid = isDateTimeValid;
                    break;
                default:
                    isValid = false;
            }
        }

        getUpdate({
            isLocked,
            type,
            stepUid,
            condition,
            dateTime,
            isValid,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLocked, type, stepUid, condition, dateTime, availableStepsKey]);

    const getTypeContent = () => {
        switch (type) {
            case CJVisualizationUnlockConditionTypeEnum.PassSelectedStep:
                return (
                    <Dropdown
                        appearance="outline"
                        inputSize="default"
                        label={t(l.Step)}
                        labelAppearance="swap"
                        placeholder={t(l.Step)}
                        value={isSelectedStepAvailable ? stepUid : null}
                        data={availableSteps}
                        onChange={stepChangeHandler}
                        isValid={isSelectedStepAvailable}
                        errorText={t(l.CannotBeEmpty, { fieldName: t(l.Step) })}
                    />
                );
            case CJVisualizationUnlockConditionTypeEnum.DateTime:
                return (
                    <ValidatableDatePickerInput
                        format={partnerDateTimeWithoutSecondsFormat}
                        withTime
                        placeholder={t(l.DateTime)}
                        value={isNil(dateTime) ? null : moment(dateTime, DateToISOStringFormat)}
                        onChange={dateTimeChangeHandler}
                        isTouched={!isDateTimeValid}
                        errorText={t(l.CannotBeEmpty, { fieldName: t(l.DateTime) })}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <CustomerJourneyVisualizationToggleSection
            label={t(l.Lock)}
            description={t(l.LockDescription)}
            isChecked={isLocked}
            onChange={setIsLocked}
        >
            <div className={styles.unlockFields}>
                <Dropdown
                    appearance="outline"
                    inputSize="default"
                    label={t(l.UnlockCondition)}
                    labelAppearance="swap"
                    placeholder={t(l.UnlockCondition)}
                    value={type}
                    data={typesData}
                    onChange={typeChangeHandler}
                    isValid={!isNil(type)}
                    errorText={t(l.CannotBeEmpty, { fieldName: t(l.UnlockCondition) })}
                />
                {getTypeContent()}
            </div>
            {type === CJVisualizationUnlockConditionTypeEnum.CJCondition && !isNil(node) && !isNil(option) && (
                <CJFilterCondition
                    hasSectionLayout={true}
                    collectedProperties={collectedProperties}
                    node={node}
                    elements={elements}
                    option={option}
                    getUpdate={conditionUpdateHandler}
                    defaultValue={condition}
                    groupLabel={t(l.Filters)}
                />
            )}
        </CustomerJourneyVisualizationToggleSection>
    );
};

CustomerJourneyVisualizationUnlock.displayName = 'CustomerJourneyVisualizationUnlock';

export default memo(CustomerJourneyVisualizationUnlock);
